import styled from 'styled-components'
import { Dayjs } from 'dayjs'
import 'dayjs/locale/pt-br'
import TextField from '@mui/material/TextField'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers'
import { makeStyles } from '@mui/styles'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { ptBR } from '@mui/x-date-pickers/locales'
import { Typography } from '@mui/material'

import CustomView from './CustomView'

const useStyles = makeStyles({
  label: {
    background: 'white'
  },
  root: {
    '& .MuiInput-input': {
      fontSize: '16px'
    },
    '& .MuiSvgIcon-fontSizeMedium': {
      fontSize: '20px'
    }
  }
})

const popperDefaultStyle = {
  '.MuiPickersCalendarHeader-label': { fontSize: '15px' },
  '.MuiDayCalendar-weekDayLabel': { fontSize: '12px' },
  '.MuiPickersDay-root': { fontSize: '12px !important' },
  '.MuiPickersYear-yearButton': { fontSize: '12px !important' },
  '.MuiPickersMonth-monthButton': { fontSize: '12px !important' }
}

const CustomDatePicker = styled(DatePicker)`
  label {
    font-size: 14px;
  }
  svg {
    font-size: 20px;
  }
`

const CustomTexField = styled(TextField)`
  width: 100%;
  height: 52px;
  font-size: 14px;

  & label.Mui-focused {
    color: #142954;
  }
  & .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border-color: #142954;
    }
  }
`

interface IPicker {
  onChange: (date: any) => void
  value: Dayjs | null
  minDate?: Date | Dayjs
  maxDate?: Date | Dayjs
  label?: string
  name: string
  id: string
  views?: any[]
  customStyle?: string
  disabled?: boolean
  errorMessage?: any
}
export default function FormDatePicker({
  onChange,
  value,
  customStyle,
  label,
  disabled,
  errorMessage,
  ...rest
}: IPicker) {
  const classes = useStyles()
  switch (customStyle) {
    case 'withBorder':
      return (
        <CustomView style={{ width: '100%' }}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="pt-br"
            localeText={
              ptBR.components.MuiLocalizationProvider.defaultProps.localeText
            }
          >
            <CustomDatePicker
              value={value}
              label={label}
              format="DD/MM/YYYY"
              onChange={(date) => onChange(date)}
              disabled={disabled}
              slotProps={{
                textField: {
                  sx: {
                    '.MuiInputBase-input': { fontSize: '14px' }
                  }
                },
                popper: {
                  sx: popperDefaultStyle
                }
              }}
              slots={{
                textField: CustomTexField
              }}
              {...rest}
            />
            <Typography fontSize="12px" color="red" mt="2px">
              {errorMessage}
            </Typography>
          </LocalizationProvider>
        </CustomView>
      )
    case 'MonthYear':
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="pt-br"
          localeText={
            ptBR.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <DatePicker
            openTo="month"
            className={classes.label}
            value={value}
            views={['month', 'year']}
            format="MM/YYYY"
            disabled={disabled}
            onChange={(date) => onChange(date)}
            slotProps={{
              popper: {
                sx: popperDefaultStyle
              },
              textField: {
                variant: "standard",
                InputProps: {
                  disableUnderline: true
                },
                style: {
                  height: '52px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  justifyContent: 'center'
                },
                classes: { root: classes.root }
              },
            }}
            slots={{
              textField: TextField
            }}
          />
        </LocalizationProvider>
      )

    default:
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="pt-br"
          localeText={
            ptBR.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <DatePicker
            className={classes.label}
            value={value}
            format="DD/MM/YYYY"
            onChange={(date) => onChange(date)}
            disabled={disabled}
            slotProps={{
              popper: {
                sx: popperDefaultStyle
              },
              textField: {
                variant: "standard",
                InputProps: {
                  disableUnderline: true
                },
                style: {
                  height: '52px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  justifyContent: 'center'
                },
                classes: { root: classes.root }
              }
            }}
            slots={{
              textField: TextField
            }}
            {...rest}
          />
        </LocalizationProvider>
      )
  }
}
